import { FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';

import { LocalizedSoldIcon } from 'dibs-elements/exports/LocalizedSoldIcon';
import { LocalizedUnavailableIcon } from 'dibs-elements/exports/LocalizedUnavailableIcon';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { PdpSharedContactSellerButton } from '../../PdpSharedContactSellerButton/PdpSharedContactSellerButton';

import { PdpRespTopModule_item$key } from './__generated__/PdpRespTopModule_item.graphql';
import { PdpRespTopModule_user$key } from './__generated__/PdpRespTopModule_user.graphql';

import styles from './PdpRespTopModule.scss';

const userFragment = graphql`
    fragment PdpRespTopModule_user on User {
        ...PdpSharedContactSellerButton_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespTopModule_item on Item {
        ...PdpSharedContactSellerButton_item
        title
        isSold
        photos {
            smallPath
        }
        seller {
            isAccessible
        }
    }
`;

export const PdpRespTopModule: FC<{
    item: PdpRespTopModule_item$key;
    user: PdpRespTopModule_user$key;
}> = ({ ...props }) => {
    const user = useFragment(userFragment, props.user);
    const item = useFragment(itemFragment, props.item);

    const { isSold, seller } = item;
    const LocalizedIcon = isSold ? LocalizedSoldIcon : LocalizedUnavailableIcon;

    return (
        <div className={styles.container} data-tn="pdp-dlos-heading-info">
            <div className={styles.imageContainer}>
                <img
                    src={item.photos?.[0]?.smallPath || ''}
                    alt={item.title || ''}
                    className={styles.image}
                />
            </div>

            <div className={styles.content}>
                <ClientSuspense fallback={<div className={styles.soldIcon} />}>
                    <LocalizedIcon className={styles.soldIcon} />
                </ClientSuspense>
                <HeadingLevel>
                    {Heading => (
                        <Heading className={styles.title} dataTn="pdp-dlos-heading-title">
                            {item.title}
                        </Heading>
                    )}
                </HeadingLevel>
            </div>
            {seller?.isAccessible && (
                <div className={styles.buttonContainer}>
                    <PdpSharedContactSellerButton
                        type="transparent"
                        size="medium"
                        launchOrigin="contact link in sold pdp - topnav"
                        user={user}
                        item={item}
                    >
                        <FormattedMessage
                            id="PdpRespTopModule.messageTheSeller"
                            defaultMessage="Message the Seller"
                        />
                    </PdpSharedContactSellerButton>
                </div>
            )}
        </div>
    );
};
